import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography, IconButton, Box, ToggleButton, ToggleButtonGroup, MenuItem
} from '@mui/material';
import { Save, Close } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { useFormik } from 'formik';
import { debounce } from 'lodash';
import { IUser } from '../../../../../models/smp';
import { SMPAPI } from '../../../../../fetch/smp';
import { ToastV3 } from '../../../components';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { DesktopDatePicker } from '@mui/lab';

const useStyles = makeStyles<Theme>(() => ({
  header: {
    backgroundColor: '#005570',
    color: '#eff4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.25rem 1rem',
    fontWeight: 600,
    fontSize: '1.02rem',
    letterSpacing: 0.2,
    borderRadius: 4,
    marginBottom: 16,
  },
}));

const denseLabelStyle = {
  fontWeight: 500,
  fontSize: '0.85rem',
  color: '#222',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
};

type Mode = 'view' | 'edit';
interface EmployeeOption { id: number; name: string; email?: string }
interface LicenseOption {
  value: string;
  label: string;
  annualCost: number;
  startDate?: string;
  endDate?: string;
  cancelByDate?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (u: IUser) => void;
  initialData?: IUser;
  applicationID: number;
}

type FormUser = Omit<IUser, 'assignedLicense' | 'accessRemoved' | 'licenseRemoved'> & {
  applicationUserID?: number;
  applicationID: number;
  employeeID?: number;
  assignedLicense: string;
  accessRemoved: Date | string | null;
  licenseRemoved: Date | string | null;
  applicationSubStatus: string;
  applicationSubStatusID?: string | number;
};

export const AddEditUserModal: React.FC<Props> = ({
  open, onClose, onSave, initialData, applicationID,
}) => {
  const classes = useStyles();
  const isNew = initialData?.applicationUserID == null;
  const [mode, setMode] = useState<Mode>(isNew ? 'edit' : 'view');


  const [licenses, setLicenses] = useState<LicenseOption[]>([]);
  const [appTierTypeOptions, setAppTierTypeOptions] = useState<{ value: string, label: string }[]>([]);
  const [showSaveReminder, setShowSaveReminder] = useState(false);

  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [employeeInput, setEmployeeInput] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [subStatusOptions, setSubStatusOptions] = useState<{ value: string, label: string }[]>([]);

  const hasInitializedEditMode = useRef(false);
  const userHasInteracted = useRef(false);



  useEffect(() => {
    if (!open) return;

    SMPAPI.fetchUserSubStatusOptions().then(setSubStatusOptions);

    SMPAPI.fetchLicenses(applicationID, { perPage: 1000, page: 1 }).then(res =>
      setLicenses(
        (res.records || []).map((l: any) => ({
          value: l.licenseID?.toString() || '',
          label: l.licenseName || `License ${l.licenseID}`,
          annualCost: Number(l.annualCost) || 0,
          startDate: l.startDate,
          endDate: l.endDate,
          cancelByDate: l.cancelByDate,
        }))
      )
    );

    SMPAPI.fetchAppTierTypeOptions().then(setAppTierTypeOptions);

    if (initialData?.userName) {
      SMPAPI.fetchEmployeesLookup(initialData.userName).then(data => {
        setEmployeeOptions(data || []);
        const found = data?.find(e => e.name === initialData.userName) || null;
        setSelectedEmployee(found);
      });
      setEmployeeInput(initialData.userName);
    } else {
      setEmployeeOptions([]);
      setSelectedEmployee(null);
      setEmployeeInput('');
    }
  }, [open, initialData, applicationID]);

  const fetchEmployees = async (query: string) => {
    setEmployeeOptions([]);
    setLoadingEmployees(true);
    try {
      const data = await SMPAPI.fetchEmployeesLookup(query);
      setEmployeeOptions(data || []);
    } catch {
      setEmployeeOptions([]);
    }
    setLoadingEmployees(false);
  };

  const debouncedFetchEmployees = useMemo(() => debounce(fetchEmployees, 300), []);

  useEffect(() => {
    if (!open) return;
    if (!employeeInput || employeeInput.length < 2) {
      setEmployeeOptions([]);
      return;
    }
    debouncedFetchEmployees(employeeInput);
  }, [employeeInput, open, debouncedFetchEmployees]);

  // Normalize initial assignedLicense
  const initialAssignedLicense: string = useMemo(() => {
    const lic = initialData?.assignedLicense;
    if (!lic) return '';
    return String(Array.isArray(lic) ? lic[0] : lic);
  }, [initialData]);

  // --- formik setup ---
  const formik = useFormik<FormUser>({
    initialValues: {
      applicationUserID: initialData?.applicationUserID,
      applicationID,
      employeeID: initialData?.employeeID,
      userID: initialData?.userID,
      userName: initialData?.userName || '',
      email: initialData?.email || '',
      roleID: initialData?.roleID || '',
      isActive: initialData?.employeeStatus ?? true,
      assignedLicense: initialAssignedLicense,
      appTierType: initialData?.appTierType || '',
      annualizedCost: (() => {
        const sel = licenses.find(l => l.value === initialAssignedLicense);
        return sel ? sel.annualCost : '';
      })(),
      dateAdded: initialData?.dateAdded ? new Date(initialData.dateAdded) : null,
      accessRemoved: initialData?.accessRemoved ? new Date(initialData.accessRemoved) : null,
      licenseRemoved: initialData?.licenseRemoved ? new Date(initialData.licenseRemoved) : null,
      approvalNotes: initialData?.approvalNotes || '',
      otherNotes: initialData?.otherNotes || '',
      applicationSubStatus: initialData?.applicationSubStatus || '',
      applicationSubStatusID: (initialData?.applicationSubStatusID !== undefined && initialData?.applicationSubStatusID !== null && initialData?.applicationSubStatusID !== '')
        ? initialData.applicationSubStatusID
        : (initialData?.applicationSubStatus
            ? (subStatusOptions.find(opt => opt.label === initialData.applicationSubStatus)?.value || '')
            : ''),
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,

    validate: values => {
      const errors: Partial<Record<keyof IUser, string>> = {};

      function ensureValidDate(field: keyof FormUser) {
        const v = values[field];
        if (v == null) return;
        const d = v instanceof Date ? v : new Date(v as string);
        if (isNaN(d.getTime())) {
          errors[field] = 'Invalid date format';
        }
      }

      ensureValidDate('dateAdded');
      ensureValidDate('accessRemoved');
      ensureValidDate('licenseRemoved');

      if (values.dateAdded && selectedLicense?.startDate) {
        if (new Date(values.dateAdded) < new Date(selectedLicense.startDate)) {
          errors.dateAdded = 'Date Added cannot be before license start date';
        }
      }

      if (values.dateAdded && values.accessRemoved && values.dateAdded > values.accessRemoved) {
        errors.dateAdded = 'Date Added cannot be after Access Removed';
        errors.accessRemoved = 'Access Removed cannot be before Date Added';
      }
      if (values.dateAdded && values.licenseRemoved && values.dateAdded > values.licenseRemoved) {
        errors.dateAdded = 'Date Added cannot be after License Removed';
        errors.licenseRemoved = 'License Removed cannot be before Date Added';
      }
      if (values.dateAdded && values.accessRemoved && values.dateAdded > values.accessRemoved) {
        errors.dateAdded = 'Date Added cannot be after Access Removed';
        errors.accessRemoved = 'Access Removed cannot be before Date Added';
      }
      if (values.dateAdded && values.licenseRemoved && values.dateAdded > values.licenseRemoved) {
        errors.dateAdded = 'Date Added cannot be after License Removed';
        errors.licenseRemoved = 'License Removed cannot be before Date Added';
      }
      return errors;
    },
    onSubmit: values => {
      const toIso = (d?: Date | string | null) =>
        d instanceof Date
          ? d.toISOString()
          : typeof d === 'string'
          ? new Date(d).toISOString()
          : undefined;

      const zeroSubStatuses = [
        "Employee Requested",
        "Waiting on LL/DH Approval",
        "Waiting for CFO Approval",
        "Waiting for Legal Review",
        "Requested from Vendor"
      ];

      const annualizedCostToSave = zeroSubStatuses.includes(values.applicationSubStatus)
        ? 0
        : values.annualizedCost;

      const userToSave = {
        ...values,
        applicationUserID: values.applicationUserID,
        applicationID,
        employeeID: values.employeeID,
        assignedLicense: values.assignedLicense,
        applicationSubStatus: values.applicationSubStatus,
        applicationSubStatusID: values.applicationSubStatusID,
        annualizedCost: annualizedCostToSave,
        dateAdded: toIso(values.dateAdded),
        accessRemoved: toIso(values.accessRemoved),
        licenseRemoved: toIso(values.licenseRemoved),
      };

      onSave(userToSave as IUser);

      setShowSaveReminder(false);
      if (isNew) {
        formik.resetForm({
          values: {
            applicationUserID: undefined,
            applicationID,
            employeeID: undefined,
            userID: undefined,
            userName: '',
            email: '',
            roleID: '',
            isActive: true,
            assignedLicense: licenses.length > 0 ? licenses[0].value : '',
            appTierType: '',
            annualizedCost: licenses.length > 0 ? licenses[0].annualCost : 0,
            dateAdded: null,
            accessRemoved: null,
            licenseRemoved: null,
            approvalNotes: '',
            otherNotes: '',
            applicationSubStatus: '',
            applicationSubStatusID: '',
          },
        });
        setSelectedEmployee(null);
        setEmployeeInput('');
        setEmployeeOptions([]);
        userHasInteracted.current = false;
        hasInitializedEditMode.current = false;
      } else {
        setMode('view');
      }
    },

  });
  const selectedLicense = licenses.find(l => l.value === formik.values.assignedLicense);

  // Sync annualizedCost when the license changes
  useEffect(() => {
    if (!open || mode !== 'edit') return;
    const selected = licenses.find(l => l.value === formik.values.assignedLicense);
    if (selected && formik.values.annualizedCost !== selected.annualCost) {
      formik.setFieldValue('annualizedCost', selected.annualCost);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.assignedLicense, licenses, open, mode]);


  // save-reminder effect
  const { dirty } = formik;
  useEffect(() => {
    if (mode !== 'edit' || !open) return;
    if (!hasInitializedEditMode.current) {
      hasInitializedEditMode.current = true;
      return;
    }
    if (!userHasInteracted.current) return;
    setShowSaveReminder(dirty);
  }, [dirty, mode, open]);

  useEffect(() => {
    if (open) {
      setMode(isNew ? 'edit' : 'view');
      hasInitializedEditMode.current = false;
      setShowSaveReminder(false);
    }
  }, [open, isNew]);

  const handleCancel = () => {
    formik.resetForm();
    setMode('view');
    setShowSaveReminder(false);
  };

  const handleEmployeeSelect = (_: any, value: EmployeeOption | null) => {
    setSelectedEmployee(value);
    userHasInteracted.current = true;

    if (value) {
      formik.setFieldValue('userName', value.name);
      formik.setFieldValue('email', value.email || `${value.name.replace(/\s+/g, '.').toLowerCase()}@dummy.com`);
      formik.setFieldValue('employeeID', value.id);
      formik.setFieldValue('isActive', (value as any).active ?? true);
    } else {
      formik.setFieldValue('userName', '');
      formik.setFieldValue('email', '');
      formik.setFieldValue('employeeID', undefined);
      formik.setFieldValue('isActive', true);
    }
  };


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2.5, borderRadius: 2 }}>
        <Typography variant="h6">
          {isNew ? 'Add User'
            : mode === 'view' ? 'User Details' : 'Edit User'}
        </Typography>
        {!isNew && (
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_, v) => v && setMode(v)}
          >
            <ToggleButton value="view" sx={{ minWidth: 72 }}>View</ToggleButton>
            <ToggleButton value="edit" sx={{ minWidth: 72 }}>Edit</ToggleButton>
          </ToggleButtonGroup>
        )}
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ px: 2.5, py: 1 }}>
          <Box bgcolor="#f5f5f5" borderRadius={2} px={2} py={2}>
            <Box mb={2}>
              <Typography className={classes.header} variant="body1">
                User Details
              </Typography>
            </Box>
            <Grid container spacing={0.5}>
              {/* Select User */}
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Select User</Typography>
              </Grid>
              <Grid item xs={5}>
                <Autocomplete
                  disabled={mode === 'view'}
                  size="small"
                  fullWidth
                  loading={loadingEmployees}
                  value={selectedEmployee}
                  inputValue={employeeInput}
                  options={employeeOptions}
                  onInputChange={(_, value) => setEmployeeInput(value)}
                  onChange={handleEmployeeSelect}
                  getOptionLabel={o => o.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                          {option.name}
                        </Typography>
                        {option.email && (
                          <Typography component="span" sx={{ fontSize: '0.94em', color: '#00008B' }}>
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search employee..."
                      helperText={selectedEmployee?.email || ''}
                      FormHelperTextProps={{ style: { margin: 2, padding: 2, lineHeight: 1.25 } }}
                    />
                  )}
                />
              </Grid>

              {/* Assign License */}
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Assign License</Typography>
              </Grid>
              <Grid item xs={5}>
                <Autocomplete
                  disabled={mode === 'view'}
                  size="small"
                  fullWidth
                  options={licenses}
                  value={licenses.find(l => l.value === formik.values.assignedLicense) || null}
                  getOptionLabel={opt => opt.label}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  onChange={(_, selected) => {
                    const newVal = selected ? selected.value : '';
                    formik.setFieldValue('assignedLicense', newVal);
                    formik.setFieldValue('annualizedCost', selected ? selected.annualCost : '');
                    userHasInteracted.current = true;
                    (['dateAdded', 'accessRemoved', 'licenseRemoved'] as const)
                      .forEach(field => formik.validateField(field));
                  }}
                  renderInput={params => <TextField {...params} placeholder="Select license" />}
                  clearOnEscape
                />
              </Grid>

              {/* App Tier Type */}
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>App Tier Type</Typography>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  disabled={mode === 'view'}
                  name="appTierType"
                  value={formik.values.appTierType || ''}
                  onChange={formik.handleChange}
                >
                  {appTierTypeOptions.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* User Application Sub Status */}
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>User Application Sub Status</Typography>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  disabled={mode === 'view'}
                  name="applicationSubStatusID"
                  value={formik.values.applicationSubStatusID || ''}
                  onChange={e => {
                    const selected = subStatusOptions.find(opt => opt.value === e.target.value);
                    formik.setFieldValue('applicationSubStatus', selected ? selected.label : e.target.value);
                    formik.setFieldValue('applicationSubStatusID', selected ? selected.value : '');
                  }}
                >
                  {subStatusOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={1} />

              {/* Date Pickers */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Date Added</Typography>
              </Grid>
              <Grid item xs={5}>

                {/* Date Added */}
                <DesktopDatePicker
                  inputFormat="M/d/yyyy"
                  disabled={mode === 'view'}
                  value={formik.values.dateAdded}
                  onChange={date => {
                    formik.setFieldValue('dateAdded', date);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('dateAdded', undefined);
                    }
                  }}
                  onAccept={date => {
                    formik.setFieldTouched('dateAdded', true);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('dateAdded', undefined);
                    }
                  }}
                  onClose={() => formik.setFieldTouched('dateAdded', true)}
                  minDate={selectedLicense?.startDate ? new Date(selectedLicense.startDate) : undefined}
                  maxDate={(() => {
                    const c = [
                      selectedLicense?.endDate && new Date(selectedLicense.endDate),
                      selectedLicense?.cancelByDate && new Date(selectedLicense.cancelByDate),
                      new Date()
                    ].filter(Boolean) as Date[];
                    return c.length ? new Date(Math.min(...c.map(d => d.getTime()))) : undefined;
                  })()}
                  renderInput={params =>
                    <TextField
                      {...params}
                      name="dateAdded"
                      fullWidth
                      size="small"
                      onBlur={formik.handleBlur}
                      error={formik.touched.dateAdded && Boolean(formik.errors.dateAdded)}
                      helperText={formik.touched.dateAdded && formik.errors.dateAdded}
                    />
                  }
                />


              </Grid>
              <Grid item xs={1} />

              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Access Removed</Typography>
              </Grid>
              <Grid item xs={5}>
                {/* Access Removed */}
                <DesktopDatePicker
                  inputFormat="M/d/yyyy"
                  disabled={mode === 'view'}
                  value={formik.values.accessRemoved}
                  onChange={date => {
                    formik.setFieldValue('accessRemoved', date);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('accessRemoved', undefined);
                    }
                  }}
                  onAccept={date => {
                    formik.setFieldTouched('accessRemoved', true);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('accessRemoved', undefined);
                    }
                  }}
                  onClose={() => formik.setFieldTouched('accessRemoved', true)}
                  minDate={formik.values.dateAdded || undefined}
                  maxDate={(() => {
                    const c = [
                      selectedLicense?.endDate && new Date(selectedLicense.endDate),
                      selectedLicense?.cancelByDate && new Date(selectedLicense.cancelByDate),
                      new Date()
                    ].filter(Boolean) as Date[];
                    return c.length ? new Date(Math.min(...c.map(d => d.getTime()))) : undefined;
                  })()}
                  renderInput={params =>
                    <TextField
                      {...params}
                      name="accessRemoved"
                      fullWidth
                      size="small"
                      onBlur={formik.handleBlur}
                      error={formik.touched.accessRemoved && Boolean(formik.errors.accessRemoved)}
                      helperText={formik.touched.accessRemoved && formik.errors.accessRemoved}
                    />
                  }
                />


              </Grid>
              <Grid item xs={1} />

              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>License Removed</Typography>
              </Grid>
              <Grid item xs={5}>

                {/* License Removed */}
                <DesktopDatePicker
                  inputFormat="M/d/yyyy"
                  disabled={mode === 'view'}
                  value={formik.values.licenseRemoved}
                  onChange={date => {
                    formik.setFieldValue('licenseRemoved', date);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('licenseRemoved', undefined);
                    }
                  }}
                  onAccept={date => {
                    formik.setFieldTouched('licenseRemoved', true);
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      formik.setFieldError('licenseRemoved', undefined);
                    }
                  }}
                  onClose={() => formik.setFieldTouched('licenseRemoved', true)}
                  minDate={formik.values.dateAdded || undefined}
                  maxDate={(() => {
                    const c = [
                      selectedLicense?.endDate && new Date(selectedLicense.endDate),
                      selectedLicense?.cancelByDate && new Date(selectedLicense.cancelByDate),
                      new Date()
                    ].filter(Boolean) as Date[];
                    return c.length ? new Date(Math.min(...c.map(d => d.getTime()))) : undefined;
                  })()}
                  renderInput={params =>
                    <TextField
                      {...params}
                      name="licenseRemoved"
                      fullWidth
                      size="small"
                      onBlur={formik.handleBlur}
                      error={formik.touched.licenseRemoved && Boolean(formik.errors.licenseRemoved)}
                      helperText={formik.touched.licenseRemoved && formik.errors.licenseRemoved}
                    />
                  }
                />

              </Grid>
              <Grid item xs={1} />

              {/* Notes */}
              <Box mt={3} mb={1.5} width="100%">
                <Typography className={classes.header} variant="body1">
                  Notes
                </Typography>
              </Box>
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Approval Notes</Typography>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  disabled={mode === 'view'}
                  name="approvalNotes"
                  value={formik.values.approvalNotes}
                  onChange={formik.handleChange}
                  multiline
                  minRows={4}
                />
              </Grid>

              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Other Notes</Typography>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  disabled={mode === 'view'}
                  name="otherNotes"
                  value={formik.values.otherNotes}
                  onChange={formik.handleChange}
                  multiline
                  minRows={4}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end' }}>
          {mode === 'edit' && (
            <>
              <Button variant="contained" onClick={handleCancel} startIcon={<Close />}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                startIcon={<Save />}
                disabled={!!formik.errors.dateAdded || !!formik.errors.accessRemoved || !!formik.errors.licenseRemoved}
              >
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </form>
      {showSaveReminder && !isNew && (
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, width: '100%', zIndex: 9999 }}>
          <ToastV3
            type="warning"
            message="You have unsaved changes."
            confirmText="Save"
            onSave={formik.submitForm}
            onClose={() => setShowSaveReminder(false)}
            onCancel={handleCancel}
            showReset={false}
          />
        </Box>
      )}
    </Dialog>
  );
};

export default AddEditUserModal;
